import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Client } from '../../types';
import api from '../../services/api';
import './ClientsList.css';

interface ClientsListProps {
  clients: Client[];
  loading: boolean;
  onClientUpdate?: () => void;
}

const ClientsList: React.FC<ClientsListProps> = ({ clients, loading, onClientUpdate }) => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [togglingStatus, setTogglingStatus] = useState<number | null>(null);
  const [localClients, setLocalClients] = useState<Client[]>(clients);

  // Update local clients when prop changes
  useEffect(() => {
    setLocalClients(clients);
  }, [clients]);

  const filteredClients = useMemo(() => {
    const clientsToFilter = localClients;
    if (!searchTerm.trim()) {
      return clientsToFilter;
    }
    const term = searchTerm.toLowerCase();
    return clientsToFilter.filter((client) => {
      return client.client_name.toLowerCase().includes(term);
    });
  }, [localClients, searchTerm]);

  const generateReviewUrl = (client: Client): string => {
    const baseUrl = window.location.origin;
    if (!client.uniq_id) {
      return `${baseUrl}/review/unknown`;
    }
    return `${baseUrl}/review/${client.uniq_id}`;
  };

  const copyToClipboard = async (text: string, clientId: number) => {
    try {
      // Try modern Clipboard API first (requires HTTPS or localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopiedId(clientId);
        setTimeout(() => setCopiedId(null), 2000);
        return;
      }
      
      // Fallback method for non-HTTPS environments
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setCopiedId(clientId);
          setTimeout(() => setCopiedId(null), 2000);
        } else {
          throw new Error('Copy command failed');
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
        // Last resort: show the URL in an alert
        alert(`Review URL: ${text}\n\nPlease copy this URL manually.`);
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: show the URL in an alert
      alert(`Review URL: ${text}\n\nPlease copy this URL manually.`);
    }
  };

  const toggleClientStatus = async (clientId: number, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setTogglingStatus(clientId);
      const response = await api.patch<Client>(`/clients/${clientId}/status`, {
        status: !currentStatus,
      });
      
      // Update local state immediately with the response
      setLocalClients(prevClients => 
        prevClients.map(client => 
          client.id === clientId ? response.data : client
        )
      );
      
      // Also refresh clients list from server to ensure consistency
      if (onClientUpdate) {
        onClientUpdate();
      }
    } catch (error: any) {
      console.error('Failed to toggle client status:', error);
      alert(error.response?.data?.message || 'Failed to toggle client status');
    } finally {
      setTogglingStatus(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading companies...</div>;
  }

  if (localClients.length === 0) {
    return <div className="no-data">No companies found</div>;
  }

  return (
    <div className="clients-list">
      <div className="search-filter">
        <input
          type="text"
          placeholder="Search by company name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <table className="clients-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Company Name</th>
            <th>Status</th>
            <th>Created Date</th>
            <th>Review URL</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredClients.length === 0 ? (
            <tr>
              <td colSpan={6} className="no-results">
                No companies found matching your search.
              </td>
            </tr>
          ) : (
            filteredClients.map((client) => {
            const reviewUrl = generateReviewUrl(client);
            // Handle both boolean (true/false) and integer (1/0) status values
            // Default to true if undefined or null
            // Explicitly check for false (boolean) or 0 (integer) to determine inactive status
            const statusValue = client.status as boolean | number | undefined;
            const isActive = statusValue !== false && statusValue !== 0;
            return (
              <tr 
                key={client.id}
                onClick={() => navigate(`/dashboard/clients/${client.id}`)}
                style={{ cursor: 'pointer' }}
                className={!isActive ? 'inactive-client' : ''}
              >
                <td>{client.id}</td>
                <td className="client-name">{client.client_name}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={(e) => toggleClientStatus(client.id, isActive, e)}
                    className={`status-toggle ${isActive ? 'active' : 'inactive'}`}
                    disabled={togglingStatus === client.id}
                    title={isActive ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {togglingStatus === client.id ? '...' : (isActive ? '✓ Active' : '✗ Inactive')}
                  </button>
                </td>
                <td>{new Date(client.created_at).toLocaleDateString()}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="review-url-container">
                    <input
                      type="text"
                      value={reviewUrl}
                      readOnly
                      className="review-url-input"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                    />
                  </div>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(reviewUrl, client.id);
                    }}
                    className="copy-button"
                    title="Copy to clipboard"
                  >
                    {copiedId === client.id ? '✓ Copied' : '📋 Copy'}
                  </button>
                </td>
              </tr>
            );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ClientsList;
